import ggVars, { toCamelCase } from 'constants/ggVars';

import * as React from 'react';

import { Autocomplete, Box, TextField } from '@mui/material';
import ChipSelect from 'components/ReportRequestV2/ChipSelect';
import FlavorConstraintPicker from 'components/ReportRequestV2/FlavorConstraintPicker';
import FormSection from 'components/ReportRequestV2/FormSection';
import RangeConstraintList from 'components/ReportRequestV2/RangeConstraintList';
import SwitchField from 'components/ReportRequestV2/SwitchField';

import { rowErrors, StepProps } from './types';
import { productKey } from '../hooks/useParentDetails';
import useReferenceFlavorLexicon from '../hooks/useReferenceFlavorLexicon';
import { CONSTRAINT_LEVELS, GGVAR_SCALE, TUNING_RANGE_PERCENT } from '../model/constants';
import { buildTokenPool, withTokenModes } from '../model/flavorTokens';
import { ConstraintLevel, NewReferenceFlavorOption, OptimizationSettings } from '../model/types';

interface Props extends StepProps {
  refFlavors: string[];
  refFlavorsLoading?: boolean;
  /** Decomp share (0–1) per reference flavor, keyed by `productKey` of each product in the parent report. */
  flavorShares?: ReadonlyMap<string, ReadonlyMap<string, number>>;
  flavorSharesLoading?: boolean;
  flavorSharesError?: { message: string };
}

const SUGGESTION_COUNT = 12;

const NO_SHARES: ReadonlyMap<string, number> = new Map();

const GGVAR_OPTIONS = ggVars.map((g) => ({
  value: toCamelCase(g.key),
  label: g.label,
}));

const toggle = <T,>(list: T[], value: T) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const OptimizationStep: React.FC<Props> = ({
  state,
  dispatch,
  errors,
  refFlavors,
  refFlavorsLoading,
  flavorShares,
  flavorSharesLoading,
  flavorSharesError,
}) => {
  const opt = state.optimization;
  const patch = (p: Partial<OptimizationSettings>) => dispatch({ type: 'patchOptimization', patch: p });
  const products = state.inherited?.competitiveSetProducts ?? [];

  const shares = (opt.targetProduct && flavorShares?.get(productKey(opt.targetProduct))) || NO_SHARES;
  const [offerAllChosen, setOfferAll] = React.useState(false);
  // With no decomp to choose from (no target yet, or no shares) every flavor is offered.
  const decompAvailable = shares.size > 0 && !flavorSharesLoading;
  const offerAll = offerAllChosen || (!decompAvailable && !flavorSharesLoading);
  const lexicon = useReferenceFlavorLexicon(offerAll);
  // Options and modes depend on which flavors are constrained, not on tune ranges,
  // so dragging a range slider doesn't rebuild and re-sort the (possibly large) list.
  const constrainedKey = [opt.refFlavorsToLock, opt.refFlavorsToRemove, opt.refFlavorsToTune.map((t) => t.flavor)]
    .map((l) => l.join('\u0000'))
    .join('\u0001');
  const lists = React.useMemo(
    () => ({
      refFlavorsToLock: opt.refFlavorsToLock,
      refFlavorsToRemove: opt.refFlavorsToRemove,
      refFlavorsToTune: opt.refFlavorsToTune,
    }),
    // Deliberately keyed on flavor membership only, see above.
    [constrainedKey],
  );
  const options = React.useMemo(
    () =>
      buildTokenPool({
        opt: lists,
        reportFlavors: refFlavors,
        shares,
        lexicon: lexicon.lexicon,
        offerAll,
      }),
    [lists, refFlavors, shares, lexicon.lexicon, offerAll],
  );
  const suggestions = React.useMemo(
    () => options.filter((o) => o.sharePercent !== undefined && o.mode === 'free').slice(0, SUGGESTION_COUNT),
    [options],
  );
  let flavorNotice: string | undefined;
  if (flavorSharesError) flavorNotice = `Flavor shares failed to load: ${flavorSharesError.message}`;
  else if (!opt.targetProduct) flavorNotice = 'Choose the product to optimize to see its top flavors and their shares.';
  else if (!flavorSharesLoading && shares.size === 0)
    flavorNotice = `No flavor shares are recorded for ${opt.targetProduct} in the parent report, so all flavors are offered.`;
  const tuneRanges = new Map(opt.refFlavorsToTune.map((t) => [t.flavor, { min: t.min, max: t.max }]));
  const tuneErrors = Object.fromEntries(
    Object.entries(rowErrors(errors, 'optimization.refFlavorsToTune')).map(([i, message]) => [
      opt.refFlavorsToTune[Number(i)]?.flavor,
      message,
    ]),
  );

  return (
    <>
      <FormSection title="Target" description="The product the optimization improves.">
        <Box sx={{ display: 'grid', gap: 3, maxWidth: 560 }}>
          <Autocomplete
            options={products}
            value={opt.targetProduct}
            onChange={(_, value) => patch({ targetProduct: value })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Product to optimize"
                required
                error={!!errors['optimization.targetProduct']}
                helperText={
                  errors['optimization.targetProduct'] ?? `${products.length} products in the parent's competitive set`
                }
              />
            )}
          />
          <Box>
            <SwitchField
              label="Optimize flavor"
              checked={opt.optimizeFlavor}
              onChange={(optimizeFlavor) => patch({ optimizeFlavor })}
            />
            <SwitchField
              label="Optimize texture"
              checked={opt.optimizeTexture}
              onChange={(optimizeTexture) => patch({ optimizeTexture })}
            />
            {errors['optimization.target'] && (
              <Box sx={{ color: 'error.main', typography: 'caption' }} role="alert">
                {errors['optimization.target']}
              </Box>
            )}
          </Box>
        </Box>
      </FormSection>

      <FormSection title="Search space" description="How far the optimizer may move away from the current recipe.">
        <Box sx={{ display: 'grid', gap: 3 }}>
          <ChipSelect
            label="Constraint levels"
            options={CONSTRAINT_LEVELS.map((c) => ({ value: c.level, label: c.label, hint: c.level }))}
            selected={opt.constraintLevels}
            onToggle={(level) =>
              patch({ constraintLevels: toggle(opt.constraintLevels, level as ConstraintLevel) })
            }
            onSelectAll={() => patch({ constraintLevels: CONSTRAINT_LEVELS.map((c) => c.level) })}
            onClear={() => patch({ constraintLevels: [] })}
            error={errors['optimization.constraintLevels']}
            helperText="One optimized version is produced per level."
          />
          <ChipSelect
            label="New reference flavors"
            options={[
              { value: 'FALSE', label: 'Existing flavors only' },
              { value: 'TRUE', label: 'Allow new flavors' },
            ]}
            selected={opt.newReferenceFlavors}
            onToggle={(v) =>
              patch({
                newReferenceFlavors: toggle(opt.newReferenceFlavors, v as NewReferenceFlavorOption),
              })
            }
            error={errors['optimization.newReferenceFlavors']}
            helperText="Selecting both runs the optimization twice."
          />
        </Box>
      </FormSection>

      <FormSection title="GGVar constraints" description="Optional. Keep a GGVar intensity within a range.">
        <RangeConstraintList
          label="GGVar constraint"
          rows={opt.ggvarConstraints}
          options={GGVAR_OPTIONS}
          onChange={(ggvarConstraints) => patch({ ggvarConstraints })}
          bounds={[GGVAR_SCALE.min, GGVAR_SCALE.max]}
          step={0.1}
          rowErrors={rowErrors(errors, 'optimization.ggvarConstraints')}
          emptyText="No GGVars are constrained."
        />
      </FormSection>

      <FormSection
        title="Reference flavors"
        description="Optional. Search the product's decomp flavors, or offer all flavors, and tune, lock or remove them. Percentages are the target product's current decomp share of each flavor."
      >
        <FlavorConstraintPicker
          options={options}
          suggestions={suggestions}
          tuneRanges={tuneRanges}
          onModeChange={(flavors, mode) => patch(withTokenModes(opt, flavors, mode))}
          onTuneRangeChange={(flavor, range) =>
            patch({ refFlavorsToTune: opt.refFlavorsToTune.map((t) => (t.flavor === flavor ? { ...t, ...range } : t)) })
          }
          tuneBounds={[TUNING_RANGE_PERCENT.min, TUNING_RANGE_PERCENT.max]}
          loading={refFlavorsLoading || flavorSharesLoading || (offerAll && lexicon.loading)}
          notice={
            refFlavors.length === 0 && !refFlavorsLoading ? 'The parent report has no reference flavors.' : flavorNotice
          }
          sharesKnown={shares.size > 0}
          offerAll={offerAll}
          onOfferAllChange={decompAvailable ? setOfferAll : undefined}
          tuneErrors={tuneErrors}
          error={errors['optimization.refFlavors']}
        />
      </FormSection>
    </>
  );
};

export default OptimizationStep;
